// Narrative content for the 14 segments of the pitch deck.
// Each segment carries display metadata + copy + 3D "state targets" that the
// SkullScene uses to interpolate (rotation, bullet depth, brain heat, jaw).

import { DECK } from '@/constants/testIds';

export const SEGMENTS = [
  {
    id: 'cold-open',
    number: '00',
    location: 'OFF THE SRI LANKAN COAST',
    title: 'BLACK AXE',
    kicker: 'RAGHAVA LAWRENCE · IN AND AS',
    director: 'WRITTEN AND DIRECTED BY ANL ARASU',
    logline: 'He survived the bullet. Now the bullet is the weapon.',
    body: 'A drifting yacht. Twenty bodies. One man kneeling in the dark with a bullet in his head and no memory of why. A new Tamil thriller — forged in salt, gold, and gunmetal.',
    emphasis: 'COMING SOON',
    rail: 'right',
    target: { yaw: 0.0, pitch: -0.05, bulletDepth: 0.02, heat: 0.0, zoom: 0.0 },
    testId: DECK.segmentColdOpen,
  },
  {
    id: 'logline',
    number: '00B',
    location: 'LOGLINE',
    title: 'LOGLINE',
    isLogline: true,
    logline:
      'A man with no name and a bullet lodged in his skull washes ashore in Sri Lanka — and every blow to his head awakens a violence even he doesn’t understand. To learn who he is, he must destroy the empire that tried to erase him.',
    rail: 'left',
    target: { yaw: -0.05, pitch: 0.02, bulletDepth: 0.05, heat: 0.04, zoom: 0.02 },
    testId: DECK.segmentLogline,
  },
  {
    id: 'synopsis',
    number: '00C',
    location: 'SYNOPSIS',
    title: 'SYNOPSIS',
    paragraphs: [
      'In the dead of night, somewhere off the Sri Lankan coast, a luxury yacht drifts in silence. Inside lies a massacre — twenty bodies, petrol-soaked, a man kneeling among the dead with tears and blood running down his face. A gunshot. A cigarette flicked from a departing boat. The yacht erupts into a fireball, and one man falls into the black water beneath the flames.',
      'He survives — barely. An old fisherman pulls him from the sea and treats his wound, but cannot remove the bullet lodged in his skull. When local police murder his rescuer, a blow to the back of his head awakens something dormant: the bullet, pressing against the part of his brain that holds violence in check, switches it off. What emerges is not a man fighting back — it is a man with no brakes.',
      'Fleeing to Rameswaram with the fisherman’s granddaughter, captured as a refugee, his second eruption is caught on camera — and suddenly every gun on the coast is pointed at a man with no name, no identity, and no memory of the massacre he walked away from.',
      'The trail leads to Thoothukudi, and to the sister of Sagayam — a man who died on that yacht. She believes Pandi killed her brother. He needs to know if she’s right. Her story reveals a friendship forged in prison, a smuggling job for a shadow syndicate called AKR, and an informer purge that ended in fire on the water.',
      'But the deepest truth lies further back. Pandi was never a prisoner. He was one of a hidden few — children raised inside prison walls and forged into undercover agents, deployed where no officer could go. His mission was to destroy the AKR consignment. Instead, it destroyed him.',
      'Now, with a bullet in his head that makes him lethal and is slowly killing him, Pandi must dismantle the AKR empire, find the man who pulled the trigger — and expose the most dangerous truth of all: the powerful cop who has been erasing undercover agents one by one, building AKR’s empire from inside the very system meant to destroy it.',
    ],
    pull: 'He survived the bullet. Now the bullet is the weapon.',
    rail: 'left',
    target: { yaw: 0.05, pitch: 0.02, bulletDepth: 0.08, heat: 0.06, zoom: 0.0 },
    testId: DECK.segmentSynopsis,
  },
  {
    id: 'night-on-the-water',
    number: '01',
    location: 'INT. LUXURY YACHT — NIGHT',
    title: 'THE NIGHT ON THE WATER',
    body: 'A luxury yacht drifts in silence. Inside lies a massacre — twenty bodies, petrol-soaked, a man kneeling among the dead with tears and blood running down his face. A gunshot. A cigarette flicked from a departing boat. The yacht erupts into a fireball, and one man falls into the black water beneath the flames.',
    pull: '‘He survives — barely.’',
    rail: 'right',
    target: { yaw: 0.15, pitch: 0.05, bulletDepth: 0.08, heat: 0.08, zoom: 0.05 },
    testId: DECK.segmentNightWater,
  },
  {
    id: 'the-bullet',
    number: '02',
    location: 'INSIDE PANDI’S SKULL',
    title: 'THE BULLET',
    body: 'An old fisherman pulls him from the sea and treats his wound — but cannot remove the bullet lodged in his skull. When local police murder his rescuer, a blow to the back of his head awakens something dormant: the bullet, pressing against the part of his brain that holds violence in check, switches it off.',
    emphasis: 'LEFT TEMPLE — VIOLENCE INHIBITOR — OFFLINE',
    pull: '‘What emerges is not a man fighting back — it is a man with no brakes.’',
    rail: 'left',
    target: { yaw: -0.25, pitch: 0.0, bulletDepth: 0.22, heat: 0.18, zoom: 0.15 },
    testId: DECK.segmentTheBullet,
  },
  {
    id: 'pandi',
    number: '03',
    location: 'CHARACTER — LEAD',
    title: 'PANDI',
    body: 'No name. No identity. No memory of the massacre he walked away from. Pandi moves like a man trying to remember how to feel grief, while every nerve in his body remembers how to kill. Raghava Lawrence brings a coiled stillness and explosive physicality to a role designed to be career-defining.',
    meta: [
      { k: 'PORTRAYED BY', v: 'RAGHAVA LAWRENCE' },
      { k: 'AGE', v: '40s' },
      { k: 'ORIGIN', v: 'UNKNOWN — RAISED INSIDE PRISON WALLS' },
      { k: 'SKILL', v: 'CLOSE-QUARTERS COMBAT, COUNTER-INTEL' },
    ],
    moodTiles: [
      { id: 'pn1', label: 'BORDER · CHECKPOINT · WHITE SHIRT',
        image: '/assets/moodboard/pandi4.png', flip: true },
      { id: 'pn2', label: 'LIGHTHOUSE · CHASE · BLUE HOUR',
        image: '/assets/moodboard/pandi3.png', flip: true },
      { id: 'pn3', label: 'BEHIND THE BARS · THE WOUND',
        image: '/assets/moodboard/pandi2.png' },
      { id: 'pn4', label: 'NO BRAKES · THE GANG · RED LIGHT',
        image: '/assets/moodboard/pandi1.png' },
    ],
    rail: 'right',
    target: { yaw: 0.35, pitch: -0.05, bulletDepth: 0.28, heat: 0.22, zoom: 0.0 },
    testId: DECK.segmentPandi,
  },
  {
    id: 'protagonist-reel',
    number: '04',
    location: 'CHARACTER REEL · 30 SECONDS',
    title: 'RAGHAVA LAWRENCE AS PANDI',
    body: 'A 30-second introduction reel anchored on Raghava Lawrence — the silence before the storm, the body that carries the bullet, the eyes that have forgotten who they were. Drop in the final cut here; the player below is ready for the master file.',
    reel: {
      runtime: '00:30',
      tag: 'PROTAGONIST · FIRST-LOOK',
      caption: 'Cold open frame — a man with no brakes, no name, no exit.',
    },
    rail: 'right',
    target: { yaw: 0.22, pitch: -0.02, bulletDepth: 0.32, heat: 0.26, zoom: 0.08 },
    testId: DECK.segmentProtagonistReel,
  },
  {
    id: 'fisherman-girl',
    number: '05',
    location: 'EXT. RAMESWARAM — DAY',
    title: 'THE FISHERMAN & THE GIRL',
    body: 'Fleeing the coast with the fisherman’s granddaughter, Pandi is captured as a refugee. His second eruption is caught on camera — and suddenly every gun on the coast is pointed at a man with no name. The girl becomes his only anchor to the human he used to be.',
    pull: '‘A grandfather’s last gift was the granddaughter who kept him alive.’',
    moodTiles: [
      { id: 'fg1', label: 'THE COMPOUND · DUSK · WATCHTOWER',
        image: '/assets/moodboard/fish2.jpg' },
      { id: 'fg2', label: 'INSIDE · REFUGEES · BLUE LIGHT',
        image: '/assets/moodboard/fish1.jpg' },
    ],
    rail: 'left',
    target: { yaw: -0.15, pitch: 0.08, bulletDepth: 0.38, heat: 0.32, zoom: 0.06 },
    testId: DECK.segmentFishermanGirl,
  },
  {
    id: 'sagayams-sister',
    number: '06',
    location: 'EXT. THOOTHUKUDI PORT — DUSK',
    title: 'SAGAYAM’S SISTER',
    body: 'The trail leads to the sister of Sagayam — one of the twenty who died on the yacht. She believes Pandi pulled the trigger. He needs to know if she is right. Her story reveals a friendship forged in prison, a smuggling job for a shadow syndicate called AKR, and an informer purge that ended in fire on the water.',
    pull: '‘Did you kill my brother?’',
    rail: 'right',
    target: { yaw: 0.2, pitch: -0.05, bulletDepth: 0.42, heat: 0.42, zoom: 0.0 },
    testId: DECK.segmentSagayamsSister,
  },
  {
    id: 'hidden-few',
    number: '07',
    location: 'CLASSIFIED — BLACK FILE / PRISON STATE PROGRAM',
    title: 'THE HIDDEN FEW',
    body: 'Pandi was never a prisoner. He was one of a hidden few — children raised inside prison walls and forged into undercover agents, deployed where no officer could go. His mission was to destroy the AKR consignment. Instead, it destroyed him.',
    pillars: [
      { k: 'ORIGIN', v: 'Born inside the system. Erased from it.' },
      { k: 'TRAINING', v: 'No academy. No record. No exit.' },
      { k: 'COST', v: 'Twenty agents on one yacht. One survivor.' },
    ],
    moodTiles: [
      { id: 'hf1', label: 'INDUCTION · BEFORE THE HUNDRED',
        image: '/assets/moodboard/hidden1.jpg' },
      { id: 'hf2', label: 'BEHIND THE BARS · LISTENING',
        image: '/assets/moodboard/hidden2_new.png' },
    ],
    rail: 'left',
    target: { yaw: -0.3, pitch: 0.0, bulletDepth: 0.5, heat: 0.5, zoom: 0.08 },
    testId: DECK.segmentHiddenFew,
  },
  {
    id: 'akr-empire',
    number: '08',
    location: 'INT. AKR FRONT OFFICES — MULTI-PORT',
    title: 'THE AKR EMPIRE',
    body: 'A shadow syndicate routed through fishing fleets, customs ledgers and burner phones — weapons, narcotics, and human cargo moving along the Tamil Nadu-Sri Lanka corridor. AKR doesn’t fear the state because AKR has the state.',
    grid: [
      { k: 'ROUTES', v: 'Mannar — Rameswaram — Thoothukudi' },
      { k: 'FRONTS', v: 'Trawlers, scrap yards, sand mafia, casinos' },
      { k: 'HIERARCHY', v: '5 captains, 1 ghost above them' },
      { k: 'BODY COUNT', v: '20 in one night — and counting' },
    ],
    rail: 'right',
    target: { yaw: 0.4, pitch: -0.08, bulletDepth: 0.58, heat: 0.6, zoom: 0.04 },
    testId: DECK.segmentAkrEmpire,
  },
  {
    id: 'police',
    number: '08B',
    location: 'EXT. NIGHT MARKET / INT. WAREHOUSE / POLICE COMMISSIONERATE — THE BADGE',
    title: 'THE POLICE · THE MAN INSIDE THE SYSTEM',
    body: 'The uniform is the empire’s sharpest mask. A khaki shirt and a star on the shoulder — a man the state trusts, a man the street fears. In the world of BLACK AXE the police are not the answer; they are the question. Every door AKR opens, the badge opened first.\n\nThe deepest truth lies further back: a powerful cop has been erasing undercover agents one by one, building AKR’s empire from inside the very system meant to destroy it. The man Pandi was meant to report to is the man who pulled the trigger.',
    moodTiles: [
      { id: 'pol1', label: 'THE BADGE IN THE CROWD',
        image: '/assets/moodboard/police2.jpg' },
      { id: 'pol2', label: 'THE THRONE · TWO HOODS · A GUN',
        image: '/assets/moodboard/police1.jpg' },
    ],
    pull: '‘The bullet didn’t come from an enemy. It came from his own commanding officer.’',
    rail: 'center',
    target: { yaw: 0.0, pitch: 0.0, bulletDepth: 0.66, heat: 0.72, zoom: -0.04 },
    testId: DECK.segmentPolice,
  },
  {
    id: 'directors-vision',
    number: '10',
    location: 'DIRECTOR’S STATEMENT · LOOK & FEEL',
    title: 'DIRECTOR’S VISION',
    body: 'A myth told in gunmetal and gold. We treat the bullet as a god and Pandi as its unwilling prophet. The film breathes through the slow heat of southern Tamil ports — brass lamps, fish-oil light, rain on tin roofs — then erupts in operatic, intimate violence. We are not making a vigilante film. We are making the autopsy of one.\n\nA palette of rust, hammered gold, oxblood and salt-bleached emerald. Costumes weathered to the bone; sets built around the smell of diesel and dried fish. The emerald-glass skull — our only futuristic motif — lives inside Pandi’s vision: a cool, otherworldly interruption in a film of brown, brass and blood.',
    pillars: [
      { k: 'TONE', v: 'Mythic, intimate, brutal.' },
      { k: 'PALETTE', v: 'Rust · hammered gold · oxblood · salt-bleached emerald.' },
      { k: 'REFERENCES', v: 'No Country for Old Men · Sicario · Old Boy · Asuran' },
      { k: 'CINEMATIC LANGUAGE', v: 'Long lenses, available light, 35mm grain.' },
    ],
    moodTiles: [
      { id: 'dv1', label: 'THE EAR-HOOK · BEHIND THE BARS',
        image: '/assets/moodboard/dv6.png' },
      { id: 'dv2', label: 'THE CONSIGNMENT · WEAPONS AT DOCK',
        image: '/assets/moodboard/dv5.jpg' },
      { id: 'dv3', label: 'THE WALK · BLACK SUIT · ARMED MEN',
        image: '/assets/moodboard/dv4.jpg' },
      { id: 'dv4', label: 'PORT-CITY DUSK · THE CONGREGATION',
        image: '/assets/moodboard/dv3.jpg' },
      { id: 'dv5', label: 'GOLDEN HOUR · GUNS IN SILHOUETTE',
        image: '/assets/moodboard/dv2.jpg' },
      { id: 'dv6', label: 'THE BRAWL · SHIPYARD · CHAOS',
        image: '/assets/moodboard/dv1.jpg' },
    ],
    rail: 'right',
    target: { yaw: 0.0, pitch: 0.0, bulletDepth: 0.74, heat: 0.78, zoom: 0.0 },
    testId: DECK.segmentDirectorsVision,
  },
  {
    id: 'moodboard',
    number: '12',
    location: 'PEOPLE · MOODBOARD',
    title: 'PEOPLE OF BLACK AXE WORLD',
    body: 'The faces that carry the weight of this world — the fisherman who pulls a stranger from the sea, the granddaughter who keeps a dying man human, the bodies left in the wake of the AKR machine, the crowds that hide Pandi when no one else can. Every shot tells you who lives here, who survives, and at what cost.',
    moodTiles: [
      { id: 'pp1', label: 'THE FISHERMAN · DAWN NETS',
        image: '/assets/moodboard/p5.jpg' },
      { id: 'pp2', label: 'ELDER · SEA · WEATHERED',
        image: '/assets/moodboard/p4.jpg' },
      { id: 'pp3', label: 'TWENTY BODIES · YACHT NIGHT',
        image: '/assets/moodboard/p3.jpg' },
      { id: 'pp4', label: 'STREET · CROWD · LANTERNS',
        image: '/assets/moodboard/p2.jpg' },
      { id: 'pp5', label: 'PANDI · IN THE CROWD',
        image: '/assets/moodboard/p1.jpg' },
    ],
    rail: 'center',
    target: { yaw: 0.05, pitch: 0.0, bulletDepth: 0.82, heat: 0.84, zoom: -0.1 },
    testId: DECK.segmentMoodboard,
  },
  {
    id: 'world-moodboard',
    number: '12B',
    location: 'WORLD · MOODBOARD',
    title: 'THE BLACK AXE WORLD',
    body: 'The places that hold the story — a luxury yacht turned coffin, a slick of burning petrol on dark water, a man sinking with a bullet in his head, and the shipyards where AKR moves whatever the empire pays for. The world of BLACK AXE is salt, steel, and fire.',
    moodTiles: [
      { id: 'wr1', label: 'THE YACHT · ARMED · BLUE HOUR',
        image: '/assets/moodboard/world1.jpg' },
      { id: 'wr2', label: 'THE FIREBALL · YACHT IGNITION',
        image: '/assets/moodboard/world2.jpg' },
      { id: 'wr3', label: 'THE SLICK · BURNING WATER',
        image: '/assets/moodboard/world3.jpg' },
      { id: 'wr4', label: 'INTO THE BLACK · ONE SURVIVOR',
        image: '/assets/moodboard/world4.png' },
      { id: 'wr5', label: 'AKR DOCKS · STEEL · DIESEL',
        image: '/assets/moodboard/world5.jpg' },
    ],
    rail: 'center',
    target: { yaw: -0.05, pitch: 0.02, bulletDepth: 0.84, heat: 0.86, zoom: -0.1 },
    testId: DECK.segmentWorldMoodboard,
  },
  {
    id: 'market-audience',
    number: '13',
    location: 'MARKET & AUDIENCE',
    title: 'MARKET & AUDIENCE',
    body: 'A theatrical-first release targeting the pan-Indian noir-thriller audience that propelled KGF, Vikram and Jailer to massive openings. Built for a 4-language simultaneous release with strong OTT and international festival tail.',
    rows: [
      { a: 'PRIMARY MARKET', b: 'Tamil Nadu, Karnataka, Kerala, Andhra/Telangana' },
      { a: 'SECONDARY MARKET', b: 'Hindi belt + diaspora (US, UK, Gulf, Malaysia)' },
      { a: 'COMPARABLES', b: 'Vikram · Jailer · Asuran · Maharaja' },
      { a: 'AUDIENCE', b: '18–49 · action-thriller · prestige-noir fans' },
      { a: 'OTT WINDOW', b: 'Tier-1 streamer post 56-day theatrical' },
    ],
    rail: 'right',
    target: { yaw: 0.2, pitch: -0.05, bulletDepth: 0.84, heat: 0.86, zoom: 0.0 },
    testId: DECK.segmentMarketAudience,
  },
  {
    id: 'production',
    number: '14',
    location: 'PRODUCTION',
    title: 'PRODUCTION',
    body: 'A 95-day production schedule across Rameswaram, Thoothukudi, Mannar (Sri Lanka) and Chennai. Anchored by a stunt and action design team with credits across major Tamil and Telugu features.',
    rows: [
      { a: 'STATUS', v: '', b: 'Pre-production' },
      { a: 'SHOOT WINDOW', v: '', b: '95 days · Q1–Q2 2026' },
      { a: 'LANGUAGES', v: '', b: 'Tamil (original) · Telugu · Hindi · Malayalam' },
      { a: 'BUDGET TIER', v: '', b: 'Mid-to-high tier theatrical' },
      { a: 'TARGET RELEASE', v: '', b: 'Q4 2026 — festive theatrical window' },
    ],
    rail: 'left',
    target: { yaw: -0.25, pitch: 0.04, bulletDepth: 0.92, heat: 0.92, zoom: 0.2 },
    testId: DECK.segmentProduction,
  },
  {
    id: 'director-credits',
    number: '15',
    location: 'DIRECTED BY',
    title: 'ANL ARASU',
    image: '/assets/director/anl-arasu.jpg',
    tagline: 'ACTION DIRECTOR · CHOREOGRAPHER · DIRECTOR',
    meta: [
      { k: 'REEL', v: '200+ FILMS' },
      { k: 'LANGUAGES', v: '5 · PAN-INDIA' },
      { k: 'STATUS', v: 'DIRECTOR · CONFIRMED' },
    ],
    paragraphs: [
      'Anl Arasu is a multi-award-winning Indian action director and choreographer with over two decades of expertise in high-octane cinema.',
      'Renowned for his gritty and realistic fight choreography, he has sculpted the action for blockbusters like Jawan, Sultan, and the Singam series.',
      'Having worked across more than 200 films in multiple languages, he is now bringing his signature high-stakes vision to the director’s chair.',
      'His mastery of cinematic violence and emotional stakes makes him the perfect architect for the world of BLACK AXE.',
    ],
    filmography: [
      { k: 'JAWAN', v: 'ACTION DESIGN' },
      { k: 'SULTAN', v: 'ACTION DESIGN' },
      { k: 'SINGAM SERIES', v: 'ACTION DESIGN' },
      { k: 'ROWDY RATHORE', v: 'ACTION DESIGN' },
      { k: 'KATHTHI', v: 'ACTION DESIGN' },
      { k: 'I', v: 'ACTION DESIGN' },
    ],
    finalCard: {
      pre: 'A FILM BY',
      name: 'ANL ARASU',
      sub: 'BLACK AXE · WESTERN LINE · MUMBAI · 2026',
    },
    rail: 'center',
    target: { yaw: 0.0, pitch: 0.0, bulletDepth: 0.95, heat: 0.92, zoom: 0.0 },
    testId: DECK.segmentDirectorCredits,
  },
  {
    id: 'contact-cta',
    number: '16',
    location: 'INVESTOR & PRODUCTION INQUIRIES',
    title: 'TAKE THE SHOT',
    body: 'A first-look slot is open to a single co-production partner and a single international distribution partner. Submit your inquiry below — we will respond within 72 hours with the full deck, budget breakdown and key crew package.',
    rail: 'center',
    target: { yaw: 0.0, pitch: 0.0, bulletDepth: 1.0, heat: 1.0, zoom: 0.0 },
    testId: DECK.segmentContactCta,
  },
  {
    id: 'closing-poster',
    number: '17',
    location: 'BLACK AXE · OFFICIAL POSTER',
    tagline: 'He survived the bullet. Now the bullet is the weapon.',
    emphasis: 'COMING SOON',
    rail: 'center',
    target: { yaw: 0.0, pitch: 0.0, bulletDepth: 1.0, heat: 1.0, zoom: 0.0 },
    testId: 'segment-closing-poster',
  },
];
